<script setup lang="ts">
  import Icon from '@leaflink/stash/Icon.vue';
  import Tooltip from '@leaflink/stash/Tooltip.vue';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';

  const companyStore = useCompanyStore();
  const { activeCompany } = storeToRefs(companyStore);

  const userStore = useUserStore();
  const { userActiveCompanyPermissions: permissions, user } = storeToRefs(userStore);

  const hasName = computed(() => !!activeCompany.value?.name);
  const isLive = computed(() => !!activeCompany.value?.isLive);
  const isBuyer = computed(() => !!activeCompany.value?.buyer);
  const isSeller = computed(() => !!activeCompany.value?.seller);

  const fullDisplayName = computed(() => {
    const address: string[] = [];
    const activeLicense = (isBuyer.value && activeCompany.value?.licenses?.[0]?.number) || '';

    if (activeCompany.value?.corporateAddress?.address) {
      address.push(activeCompany.value.corporateAddress.address);
    }

    if (activeCompany.value?.corporateAddress?.state) {
      address.push(activeCompany.value.corporateAddress.state);
    }

    const id = activeCompany.value?.businessIdentifier || activeLicense || '';

    return `${activeCompany.value?.name} (${address.join(', ')}) ${id}`;
  });
</script>

<template>
  <div v-if="hasName" class="company-status" data-test="app-header|company-status">
    <template v-if="isLive">
      <span>{{ i18n.t('layout.companyStatus.currentlyManaging', { company: fullDisplayName }) }}</span>

      <template v-if="activeCompany?.isPre2018Ca && !user.featureFlags.enableServicesMarketplace">
        <router-link
          v-if="permissions?.staffIsAdmin"
          class="tw-p-0 tw-text-red-500"
          :to="{ name: 'licenses', params: { company: activeCompany?.slug } }"
        >
          {{ i18n.t('layout.companyStatus.updateLicenses') }}
        </router-link>
        <span v-else class="tw-text-red-500">
          {{ i18n.t('layout.companyStatus.updateLicenses') }}
        </span>
      </template>
    </template>
    <div v-else class="tw-text-red-500">
      {{ i18n.t('layout.companyStatus.companyNotLive', { company: fullDisplayName }) }}
      <Tooltip
        :text="
          isSeller
            ? i18n.t('layout.companyStatus.companyNotLiveTooltipSeller')
            : i18n.t('layout.companyStatus.companyNotLiveTooltipBuyer')
        "
      >
        <Icon name="circle-question-mark" />
      </Tooltip>
    </div>
  </div>
</template>
<style lang="scss" scoped>
  .company-status {
    display: none;

    @include breakpoint('xl') {
      display: flex;
    }
  }
</style>
